import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FaArrowLeft, FaCogs, FaCheck } from "react-icons/fa";

import {
  useGetOtherSettingsQuery,
  useUpdateOtherSettingsMutation,
} from "../toolkitServices/otherSettingsApi";

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.3 },
  },
});

const OtherSettings = () => {
  const { data } = useGetOtherSettingsQuery();
  const settings = data?.data;

  const [updateSettings, { isLoading }] = useUpdateOtherSettingsMutation();

  const handleChange = async (mode) => {
    try {
      await updateSettings({ manualAdjustmentMode: mode }).unwrap();
      toast.success("Setting updated");
    } catch {
      toast.error("Failed to update setting");
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        variants={fadeUp(0)}
        initial="hidden"
        animate="visible"
        className="bg-white rounded-xl shadow-sm border px-3 py-3 flex items-center gap-3"
      >
        <Link
          to="/dashboard"
          className="h-9 w-9 flex items-center justify-center border rounded-full hover:bg-gray-100"
        >
          <FaArrowLeft className="text-violet-600 text-sm" />
        </Link>

        <div>
          <h1 className="text-lg font-semibold text-violet-700">
            Other Settings
          </h1>
          <p className="text-xs text-gray-500">Configure system behavior</p>
        </div>
      </motion.div>

      {/* Main Layout */}
      <motion.div
        variants={fadeUp(0.1)}
        initial="hidden"
        animate="visible"
        className="mt-3 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-3"
      >
        {/* Left Card */}
        <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-3 py-3 bg-violet-500 text-white flex items-center gap-2">
            <FaCogs />
            <span className="font-semibold text-sm">Settings</span>
          </div>

          <div className="p-4 text-xs text-gray-500">
            Manage how manual amount adjustments behave in invoice & estimate.
          </div>
        </div>

        {/* Right Card */}
        <div className="bg-white border rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 text-sm font-semibold text-gray-700">
            Manual Amount Adjustment
          </div>

          <div className="p-4 space-y-4">
            {/* Radio Options */}
            {[
              { label: "Discount", value: "DISCOUNT" },
              { label: "Making %", value: "MAKING" },
              { label: "Making per Gram", value: "MAKINGPERGRAM" },
              { label: "Fixed Amount", value: "FIXEDAMOUNT" },
              { label: "Fixed Wastage", value: "FIXEDWASTAGE" },
            ].map((opt) => (
              <label
                key={opt.value}
                className="flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer hover:bg-violet-50"
              >
                <span className="text-sm font-medium text-gray-700">
                  {opt.label}
                </span>

                <input
                  type="radio"
                  name="manualAdjustmentMode"
                  value={opt.value}
                  checked={settings?.manualAdjustmentMode === opt.value}
                  onChange={() => handleChange(opt.value)}
                  className="accent-violet-600"
                />
              </label>
            ))}
          </div>

          {/* Save indicator */}
          <div className="px-4 py-3 border-t text-xs text-gray-500 flex justify-end">
            {isLoading ? "Updating..." : "Changes auto-saved"}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default OtherSettings;
